import React, { useState, useEffect, useRef, useMemo, useId } from 'react';
import {
    TextField as MuiTextField,
    Autocomplete,
    AutocompleteRenderInputParams,
    InputAdornment,
    Typography,
    Skeleton,
} from '@mui/material';
import { fieldToAutocomplete, AutocompleteProps } from 'formik-mui';

import { useGetDataQuery } from '../../../app/store';
import { CityOption } from './types';
import { provinceTransform } from './utils';
import { ListboxComponent, StyledPopper } from './virtualization';
import useFocusOnError from '../use-focus-on-error';
import { getIn } from 'formik';

const CitySelector: React.FC<any> = (
    props: AutocompleteProps<any, undefined, undefined, undefined> & {
        label: string;
        icon: any;
        helpertext?: string;
        isloading?: string;
        liveValidation?: boolean;
        disableAutoFill?: boolean;
    },
) => {
    const [options, updateOptions] = useState<any>([]);
    const [inputFocused, setInputFocused] = useState(false);
    const { data } = useGetDataQuery();
    const ref = useRef();
    const autoFillId = useId().replace(/:/g, '');

    const { liveValidation = true, disableAutoFill = false, ...formikProps } =
        props as any;
    const {
        form: {
            setFieldValue,
            validateField,
            touched,
            errors,
            status,
            submitCount,
            validateOnBlur,
            validateOnChange,
        },
        field: { name, value },
        label,
        icon,
        helpertext = '',
        isloading = 'false',
    } = formikProps;

    useFocusOnError({
        fieldRef: ref,
        name,
    });

    const fieldTouched = getIn(touched, name);
    const fieldError = getIn(errors, name);
    const fieldStatus = getIn(status, name);
    const deferValidationUntilSubmit =
        validateOnBlur === false && validateOnChange === false;
    const canShowValidation = deferValidationUntilSubmit
        ? submitCount > 0
        : Boolean(fieldTouched);

    const memoizedOptions = useMemo(() => {
        if (data?.provinces) {
            return provinceTransform(data.provinces);
        }
        return [];
    }, [data?.provinces]);

    useEffect(() => {
        updateOptions(memoizedOptions);
    }, [memoizedOptions]);

    const onChange: any = React.useCallback(
        (event: any, option: CityOption) => {
            setFieldValue(name, option ? String(option.value) : '');
        },
        [setFieldValue, name],
    );

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!liveValidation) return;
        if (deferValidationUntilSubmit && submitCount === 0) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => validateField(name), 500);
        return () => clearTimeout(timeoutRef.current!);
    }, [
        liveValidation,
        name,
        validateField,
        value,
        deferValidationUntilSubmit,
        submitCount,
    ]);

    const autocompleteFieldProps = fieldToAutocomplete(formikProps);

    const onFocus: any = React.useCallback((event: any) => {
        setInputFocused(true);
        if (event.target.autocomplete) {
            event.target.autocomplete = 'whatever';
        }
    }, []);

    const onBlur: any = React.useCallback(
        (event: any) => {
            setInputFocused(false);
            autocompleteFieldProps.onBlur?.(event);
        },
        [autocompleteFieldProps.onBlur],
    );

    const selectedOption =
        options.find((option: CityOption) => String(option.value) === String(value)) || null;

    return (
        <Autocomplete
            {...autocompleteFieldProps}
            options={options}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            value={selectedOption}
            disableListWrap
            PopperComponent={StyledPopper}
            ListboxComponent={ListboxComponent}
            isOptionEqualToValue={(option: CityOption, value: CityOption) =>
                String(option.value) === String(value.value)
            }
            groupBy={(option: CityOption) => option.group}
            getOptionLabel={(option: CityOption) => option.label}
            renderGroup={(params) => params as any}
            renderOption={(props, option, state) =>
                [props, option, state.index] as React.ReactNode
            }
            renderInput={(params: AutocompleteRenderInputParams) => (
                <>
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="56px"
                        sx={{ display: isloading === 'true' ? 'flex' : 'none' }}
                    />

                    <MuiTextField
                        {...params}
                        id={
                            disableAutoFill
                                ? `address-city-${autoFillId}`
                                : undefined
                        }
                        name={
                            disableAutoFill
                                ? `__address_city_${autoFillId}`
                                : name
                        }
                        error={
                            canShowValidation &&
                            Boolean(fieldError || Boolean(fieldStatus))
                        }
                        helperText={
                            canShowValidation
                                ? fieldError || fieldStatus || helpertext
                                : helpertext
                        }
                        variant="outlined"
                        fullWidth
                        label={label}
                        inputRef={ref}
                        InputLabelProps={{
                            shrink: inputFocused || Boolean(selectedOption),
                        }}
                        autoComplete={disableAutoFill ? 'new-password' : 'off'}
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: disableAutoFill
                                ? 'new-password'
                                : params.inputProps?.autoComplete,
                            name: disableAutoFill
                                ? `__address_city_${autoFillId}`
                                : params.inputProps?.name,
                            ...(disableAutoFill
                                ? {
                                      'data-lpignore': 'true',
                                      'data-1p-ignore': 'true',
                                      'data-form-type': 'other',
                                      autoCapitalize: 'off',
                                      autoCorrect: 'off',
                                      spellCheck: false,
                                  }
                                : {}),
                        }}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    {icon}
                                </InputAdornment>
                            ),
                            sx: {
                                '&.MuiInputBase-root svg': {
                                    color: 'text.primary',
                                },
                            },
                        }}
                        sx={{
                            display:
                                isloading === 'true' ? 'none' : 'inline-flex',
                        }}
                    />
                </>
            )}
            filterOptions={(options, state) =>
                options.filter((option: any) =>
                    option.label
                        .toLowerCase()
                        .includes(state.inputValue.toLowerCase()),
                )
            }
            noOptionsText={
                <Typography sx={{ color: 'text.primary' }}>
                    هیچ شهری یافت نشد
                </Typography>
            }
        />
    );
};

export default CitySelector;
